import {Button, CircularProgress, Stack, TextField, Typography} from "@mui/material";
import React, {useMemo, useState} from "react";
import {useAuth} from "../../../../../contexts/AuthContext";
import {useSnackbar} from "../../../../../contexts/SnackbarContext";
import {useTranslation} from "react-i18next";
import {LicenseActivationForm, LicenseAdminActivationContainer,} from "./LicenseAdminActivationContent.styled";
import FileDropzone from "../../../../../components/common/FileDropzone";
import {licenseService} from "../../../../../services/license.service";
import {allowExtensions, composeValidators, maxCount} from "../../../../../utils/fileValidation";
import {useLicenseValidation} from "../../../../../contexts/LicenseProvider";
import axios from "axios";

const LicenseAdminActivationContent = () => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const { revalidateLicense } = useLicenseValidation();

    const {logout} = useAuth();

    const [keyValue, setKeyValue] = useState<string>('');
    const [files, setFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const validateLicenseFile = useMemo(
        () => composeValidators(
            allowExtensions(
                ['.txt'],
                t('licenseActivation.activation.wrongFileFormatError') as string
            ),
            maxCount(1, t('licenseActivation.activation.tooManyFilesError') as string),
        ),
        [t]
    );

    const handleSubmitInput = async () => {
        if (files.length === 0 && keyValue.trim().length === 0) return;
        const isKeyLicense = keyValue.trim().length > 0;

        try {
            setIsSubmitting(true);

            if (isKeyLicense) {
                await licenseService.activateLicenseByKey(keyValue);
            }
            else {
                const file = files[0];
                await licenseService.activateLicenseByFile(file);
            }

            showSnackbar(t('licenseActivation.activation.activateLicenseSuccess'), "success");

            await revalidateLicense();
        } catch(error) {
            if (axios.isCancel(error)) return;

            console.error(error);
            showSnackbar(t(isKeyLicense
                ? 'licenseActivation.activation.activateLicenseWithKeyError'
                : 'licenseActivation.activation.activateLicenseWithFileError'
            ), "error");
        } finally {
            setFiles([]);
            setKeyValue('');
            
            setIsSubmitting(false);
        }
    };

    const handleReject = (message: string) => showSnackbar(message, 'error');

    const handleClearInputs = () => {
        setFiles([]);
        setKeyValue('');
    };

    return (
        <LicenseAdminActivationContainer>
            <Typography id="license-required-title" variant="h4" color="text.primary" sx={{ mb: 2 }}>
                {t('licenseActivation.activation.title')}
            </Typography>

            <Typography variant="subtitle1" color="text.primary">
                {t('licenseActivation.activation.description')}
            </Typography>

            <LicenseActivationForm>
                <Stack direction="column" spacing={2} mt={2}>
                    <Typography variant="caption" component="p" textTransform="uppercase" fontWeight="medium">
                        {t('licenseActivation.activation.licenseFileLabel')}
                    </Typography>

                    <FileDropzone
                        value={files}
                        accept={['.txt','text/plain']}
                        multiple={false}
                        onChange={setFiles}
                        onReject={handleReject}
                        validate={validateLicenseFile}
                        disabled={keyValue.trim().length > 0 || isSubmitting}
                        title={t('licenseActivation.activation.dragDropArea') as string}
                        formatHint={t('licenseActivation.activation.formatsInfo') as string}
                    />
                </Stack>

                <Stack direction="column" spacing={2} mt={3} mb={3}>
                    <Typography variant="caption" component="p" textTransform="uppercase" fontWeight="medium">
                        {t('licenseActivation.activation.licenseKeyLabel')}
                    </Typography>

                    <TextField
                        value={keyValue}
                        onChange={(e) => setKeyValue(e.target.value)}
                        variant="outlined"
                        placeholder={t('licenseActivation.activation.keyPlaceholder') as string ?? ''}
                        multiline
                        minRows={5}
                        fullWidth
                        disabled={files.length > 0 || isSubmitting}
                    />
                </Stack>
            </LicenseActivationForm>


            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                <Button type="button" variant="text" onClick={() => logout()} disabled={isSubmitting}>
                    {t('licenseActivation.activation.logoutBtn')}
                </Button>

                <Stack direction="row" spacing={1}>
                    <Button type="button"
                            variant="text"
                            disabled={isSubmitting || (files.length === 0 && keyValue.trim().length === 0)}
                            onClick={handleClearInputs}
                    >
                        {t('common.clear')}
                    </Button>

                    <Button type="button"
                            variant="contained"
                            disabled={isSubmitting || (files.length === 0 && keyValue.trim().length === 0)}
                            onClick={handleSubmitInput}
                    >
                        {isSubmitting ? <CircularProgress /> : t('licenseActivation.activation.activateLicenseBtn')}
                    </Button>
                </Stack>
            </Stack>
        </LicenseAdminActivationContainer>
    );
};

export default LicenseAdminActivationContent;